import { Injectable, signal, computed, inject } from '@angular/core';
import { MobileScaleService } from './mobile-scale.service';

const SPEED_STORAGE_KEY = 'animation-speed';
const SKIP_STORAGE_KEY = 'skip-animations';
const CONDENSED_LOGS_STORAGE_KEY = 'condensed-battle-logs';
const SKIP_CONTROLS_CONFIRM_STORAGE_KEY = 'skip-controls-confirm';
const LEGACY_SKIP_MON_CONFIRM_STORAGE_KEY = 'skip-mon-confirm';
const DEFAULT_SPEED = 1.0;
const MIN_SPEED = 0.25;
const MAX_SPEED = 4.0;
// Desktop chunks messages at 100 chars vs mobile's 38, so a line almost never
// wraps there and reads the full beat — pace desktop faster to compensate.
const BASE_DURATION_DESKTOP = 900;
const BASE_DURATION_MOBILE = 1200;

@Injectable({ providedIn: 'root' })
export class AnimationSpeedService {
  private mobile = inject(MobileScaleService);

  readonly speed = signal(this.loadSpeed());

  private readonly _skipPref = signal(localStorage.getItem(SKIP_STORAGE_KEY) === 'true');
  readonly condensedBattleLogs = signal(this.loadCondensedDefault());
  // Desktop only: commit control picks on first click — skips the swap-detail confirm
  // and auto-submits the doubles turn once every acting lane is staged.
  readonly skipControlsConfirm = signal(
    (localStorage.getItem(SKIP_CONTROLS_CONFIRM_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_SKIP_MON_CONFIRM_STORAGE_KEY)) === 'true',
  );
  // Replay seek/step/pause flips this on so the in-flight turn races to its end.
  private readonly _scrubbing = signal(false);

  readonly skipAnimations = computed(() => this._scrubbing() || this._skipPref());

  private readonly baseDuration = this.mobile.isMobile
    ? BASE_DURATION_MOBILE
    : BASE_DURATION_DESKTOP;

  readonly battleDuration = computed(() =>
    this._scrubbing() ? 0 : Math.round(this.baseDuration / this.speed()),
  );

  setSpeed(value: number): void {
    const clamped = Math.max(MIN_SPEED, Math.min(MAX_SPEED, value));
    this.speed.set(clamped);
    localStorage.setItem(SPEED_STORAGE_KEY, clamped.toString());
  }

  setSkipAnimations(value: boolean): void {
    this._skipPref.set(value);
    localStorage.setItem(SKIP_STORAGE_KEY, value.toString());
  }

  setCondensedBattleLogs(value: boolean): void {
    this.condensedBattleLogs.set(value);
    localStorage.setItem(CONDENSED_LOGS_STORAGE_KEY, value.toString());
  }

  setSkipControlsConfirm(value: boolean): void {
    this.skipControlsConfirm.set(value);
    localStorage.setItem(SKIP_CONTROLS_CONFIRM_STORAGE_KEY, value.toString());
  }

  setScrubbing(value: boolean): void {
    this._scrubbing.set(value);
  }

  private loadSpeed(): number {
    const stored = localStorage.getItem(SPEED_STORAGE_KEY);
    if (stored) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed >= MIN_SPEED && parsed <= MAX_SPEED) {
        return parsed;
      }
    }
    return DEFAULT_SPEED;
  }

  private loadCondensedDefault(): boolean {
    const stored = localStorage.getItem(CONDENSED_LOGS_STORAGE_KEY);
    if (stored !== null) return stored === 'true';
    return this.mobile.isMobile;
  }
}
